import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from "react";
import { Timer, Play } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Textarea } from "@/components/ui/Textarea";
import { SearchableCombobox } from "@/components/ui/SearchableCombobox";
import { getDataAdapter } from "@/data";
import { rateHint } from "@/utils/rates";
import { durationRule, durationUnit } from "@/utils/duration";
const regardingOptions = [
    { value: "account", label: "Account" },
    { value: "msdyn_workorder", label: "Work Order" },
    { value: "msdyn_project", label: "Project" },
];
// Mirrors the OOB msdyn_timeentrytype global choice (see EntryType / TYPE_TO_CODE).
const typeOptions = [
    { value: "Work", label: "Work" },
    { value: "Travel", label: "Travel" },
    { value: "On Break", label: "On Break" },
    { value: "Overtime", label: "Overtime" },
    { value: "Absence", label: "Absence" },
    { value: "Vacation", label: "Vacation" },
];
export function StartTimerDialog({ onClose, onStart }) {
    const adapter = getDataAdapter();
    const [description, setDescription] = useState("");
    const [regardingType, setRegardingType] = useState("msdyn_workorder");
    const [regardingId, setRegardingId] = useState(null);
    const [taskId, setTaskId] = useState(null);
    const [bookingId, setBookingId] = useState(null);
    const [tcId, setTcId] = useState(null);
    const [roleId, setRoleId] = useState(null);
    const [entryType, setEntryType] = useState("Work");
    const [internalDescription, setInternalDescription] = useState("");
    const [externalComments, setExternalComments] = useState("");
    const [accounts, setAccounts] = useState([]);
    const [workOrders, setWorkOrders] = useState([]);
    const [projects, setProjects] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [transCats, setTransCats] = useState([]);
    const [resCats, setResCats] = useState([]);
    useEffect(() => {
        void Promise.all([
            adapter.listAccounts(true),
            adapter.listWorkOrders(),
            adapter.listProjects(),
            adapter.listTransactionCategories(),
            adapter.listResourceCategories(),
        ]).then(([a, w, p, tc, rc]) => {
            setAccounts(a);
            setWorkOrders(w);
            setProjects(p);
            setTransCats(tc);
            setResCats(rc);
        });
    }, [adapter]);
    useEffect(() => {
        if (regardingType === "msdyn_project" && regardingId) {
            void adapter.listProjectTasks(regardingId).then(setTasks);
        }
        else {
            setTasks([]);
            setTaskId(null);
        }
    }, [adapter, regardingId, regardingType]);
    // Bookings live under work orders in FS — only load when a WO is picked.
    useEffect(() => {
        if (regardingType === "msdyn_workorder" && regardingId) {
            void adapter.listBookings(regardingId).then(setBookings);
        }
        else {
            setBookings([]);
            setBookingId(null);
        }
    }, [adapter, regardingId, regardingType]);
    useEffect(() => {
        setRegardingId(null);
        setTaskId(null);
        setBookingId(null);
    }, [regardingType]);
    const regardingItems = useMemo(() => {
        if (regardingType === "account")
            return accounts.map((a) => ({ value: a.accountid, label: a.name, hint: a.industry }));
        if (regardingType === "msdyn_workorder")
            return workOrders.map((w) => ({
                value: w.msdyn_workorderid,
                label: w.msdyn_name,
                hint: accounts.find((a) => a.accountid === w.msdyn_serviceaccount)?.name,
            }));
        return projects.map((p) => ({
            value: p.msdyn_projectid,
            label: p.msdyn_subject,
            hint: accounts.find((a) => a.accountid === p.msdyn_customer)?.name,
        }));
    }, [regardingType, accounts, workOrders, projects]);
    const canStart = description.trim().length > 0 && (entryType !== "Work" || regardingId !== null);
    const handleStart = () => {
        let regarding;
        if (regardingId) {
            const display = regardingItems.find((i) => i.value === regardingId)?.label ?? "";
            regarding = { type: regardingType, id: regardingId, display };
        }
        onStart({
            startedAt: new Date().toISOString(),
            description: description.trim(),
            type: entryType,
            regarding,
            transactionCategoryId: tcId ?? undefined,
            resourceCategoryId: roleId ?? undefined,
            projectTaskId: taskId ?? undefined,
            bookingId: bookingId ?? undefined,
            internalDescription: internalDescription.trim() || undefined,
            externalComments: externalComments.trim() || undefined,
        });
    };
    return (_jsxs(Modal, { open: true, onClose: onClose, icon: _jsx(Timer, { size: 18 }), title: "Start a timer", subtitle: "Capture work as you do it. Stop the timer to save the entry.", footer: _jsxs(_Fragment, { children: [_jsx(Button, { variant: "ghost", onClick: onClose, children: "Cancel" }), _jsx(Button, { onClick: handleStart, disabled: !canStart, leftIcon: _jsx(Play, { size: 14, fill: "currentColor" }), children: "Start timer" })] }), children: [_jsx(Input, { label: "Name", placeholder: "What are you working on?", value: description, onChange: (e) => setDescription(e.target.value) }), _jsxs("p", { className: "text-xs text-slate-500", children: ["On stop: ", durationRule(durationUnit(tcId, transCats))] }), _jsxs("div", { className: "grid grid-cols-1 sm:grid-cols-2 gap-3", children: [_jsx(Select, { label: "Regarding type", value: regardingType, onChange: (e) => setRegardingType(e.target.value), options: regardingOptions }), _jsx(SearchableCombobox, { label: "Regarding", items: regardingItems, value: regardingId, onChange: setRegardingId, placeholder: regardingType === "account"
                            ? "Select an account"
                            : regardingType === "msdyn_workorder"
                                ? "Select a work order"
                                : "Select a project" })] }), regardingType === "msdyn_project" && regardingId && (_jsx(SearchableCombobox, { label: "Project task", items: tasks.map((t) => ({ value: t.msdyn_projecttaskid, label: t.msdyn_subject })), value: taskId, onChange: setTaskId, placeholder: "Select a task" })), regardingType === "msdyn_workorder" && regardingId && (_jsx(SearchableCombobox, { label: "Booking", items: bookings.map((b) => ({ value: b.bookableresourcebookingid, label: b.name })), value: bookingId, onChange: setBookingId, placeholder: "Optional \u2014 link to a scheduled appointment" })), _jsxs("div", { className: "grid grid-cols-1 sm:grid-cols-2 gap-3", children: [_jsx(SearchableCombobox, { label: "Service type", items: transCats.map((t) => ({
                            value: t.msdyn_transactioncategoryid,
                            label: t.msdyn_description,
                            hint: rateHint(t),
                        })), value: tcId, onChange: setTcId, placeholder: "Optional" }), _jsx(SearchableCombobox, { label: "Role", items: resCats.map((r) => ({
                            value: r.bookableresourcecategoryid,
                            label: r.name,
                            hint: `$${r.hourlyRate}/hr`,
                        })), value: roleId, onChange: setRoleId, placeholder: "Optional \u2014 fallback rate" })] }), _jsx(Select, { label: "Type", value: entryType, onChange: (e) => setEntryType(e.target.value), options: typeOptions }), _jsx(Textarea, { label: "Details of Work", hint: "internal notes, not customer-visible", placeholder: "Extended notes for the team (optional)", value: internalDescription, onChange: (e) => setInternalDescription(e.target.value), rows: 2, maxLength: 2000 }), _jsx(Textarea, { label: "External comments", hint: "appears on the customer invoice \u2014 100 character limit", placeholder: "Customer-facing notes that flow onto the invoice (optional)", value: externalComments, onChange: (e) => setExternalComments(e.target.value), rows: 2, maxLength: 100 })] }));
}
